import {
  threadRecordQuery,
  ThreadsQueryThread,
} from "@/app/Threads/GraphQL/threadQueries";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { Link } from "@tanstack/react-router";
import { ThreadClosedBanner } from "@/app/Threads/Components/ThreadClosedBanner";
import { ThreadMembers } from "@/app/Threads/Components/ThreadMembers";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useThreadActions } from "@/app/Threads/hooks/useThreadActions";
import { useSetThreadNotifications } from "@/app/Threads/hooks/useSetThreadNotifications";
import { BellIcon as BellIconSolid } from "@heroicons/react/24/solid";
import { BellSlashIcon, BellIcon } from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { ActivityIcon } from "lucide-react";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useShowThreadActivity } from "@/app/Threads/Components/ThreadActivityProvider";
import { ActionGroupButton } from "@/lib/Components/Actions/ActionGroupButtons";
import { Badge } from "@/components/catalyst/badge";
import { workOrderStatusMap } from "@/app/ProDrivers/WorkOrders/utils/workOrderStatusMap";

export function ThreadHeader({
  id,
  hasUnread,
  showWorkOrderDetails = true,
}: {
  id: string;
  hasUnread?: boolean;
  showWorkOrderDetails?: boolean;
}) {
  const { data: threadData } = useSuspenseGqlQuery(threadRecordQuery, {
    id: id,
  });

  const thread = threadData?.thread;

  if (!thread) return null;

  return (
    <>
      <div className="relative flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 px-4 py-3 lg:justify-center lg:gap-x-6">
        {showWorkOrderDetails && (
          <>
            <ThreadableLink thread={thread} />
            <ThreadableTitle thread={thread} />
            <ThreadableStatusBadge thread={thread} />
          </>
        )}
        <ThreadMembers thread={thread} size={7} />
        {thread.closed ? <ClosedBadge /> : null}
        <div className="max-lg:ml-auto lg:absolute lg:right-2 lg:top-1/2 lg:-translate-y-1/2">
          <ThreadActions id={id} />
        </div>
        {hasUnread ? <UnreadBadge /> : null}
      </div>
      <ThreadClosedBanner closed={thread.closed} />
    </>
  );
}

export function ClosedBadge() {
  const t = useTranslations("thread.closed");

  return (
    <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
      {t("badge")}
    </span>
  );
}

export function UnreadBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
      <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
      New message(s)
    </span>
  );
}

export function ThreadActions({ id }: { id: string }) {
  const { data: threadData } = useSuspenseGqlQuery(threadRecordQuery, {
    id: id,
  });
  const getActions = useThreadActions();
  const activityAction = useThreadActivityAction();

  const thread = threadData?.thread;

  if (!thread) return null;

  return (
    <div className="flex items-center gap-1">
      <NotificationBell threadId={id} thread={thread} />
      <IconMenuActions
        actions={[
          ...getActions({ id, closed: thread.closed }),
          ...activityAction,
        ]}
      />
    </div>
  );
}

/**
 * Not part of useThreadActions: that hook also feeds the context menu on a
 * thread row, and how the open conversation is displayed is nothing a row can
 * answer for.
 */
function useThreadActivityAction(): ActionGroupButton[] {
  const t = useTranslations("thread.activity");
  const { isAdmin } = useTenant();
  const activity = useShowThreadActivity();

  if (!isAdmin || activity === null) {
    return [];
  }

  return [
    {
      label: t("title"),
      items: [
        {
          actions: [
            {
              label: activity.showActivity ? t("hide") : t("show"),
              Icon: ActivityIcon,
              handleAction: activity.toggleActivity,
            },
          ],
        },
      ],
    },
  ];
}

function NotificationBell({
  threadId,
  thread,
}: {
  threadId: string;
  thread: ThreadsQueryThread;
}) {
  const { mutate, isPending } = useSetThreadNotifications();
  const enabled = thread.subscription?.notifications_enabled ?? false;
  const label = enabled ? "Disable notifications" : "Enable notifications";

  function handleClick() {
    const next = !enabled;
    mutate(
      { thread_id: threadId, enabled: next },
      {
        onError: () => toast.error("Failed to update notifications"),
      },
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          disabled={isPending}
          aria-label={label}
          className={cn(
            "rounded-md p-1.5 transition-colors hover:bg-gray-100 disabled:opacity-50",
            enabled ? "text-blue-500" : "text-gray-400",
          )}
        >
          {enabled ? (
            <BellIconSolid className="size-5" />
          ) : thread.subscription ? (
            <BellSlashIcon className="size-5" />
          ) : (
            <BellIcon className="size-5" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function ThreadableLink({ thread }: { thread: ThreadsQueryThread }) {
  if (thread.threadable?.__typename !== "WorkOrder") return null;

  const workOrderId = thread.threadable.id;

  return (
    <Link
      from="/$tenant/$orgId/supplier/{-$supplierId}"
      to={"/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"}
      params={(prev) => ({ ...prev, id: workOrderId })}
      className="text-sm font-medium text-blue-500 hover:underline"
    >
      {thread.threadable.reference}
    </Link>
  );
}

function ThreadableStatusBadge({ thread }: { thread: ThreadsQueryThread }) {
  const t = useTranslations("workOrder");

  if (thread.threadable?.__typename !== "WorkOrder") return null;

  const status = thread.threadable.status;

  return (
    <Badge color={workOrderStatusMap[status]}>{t(`status.${status}`)}</Badge>
  );
}

function ThreadableTitle({ thread }: { thread: ThreadsQueryThread }) {
  if (thread.threadable?.__typename !== "WorkOrder") return null;

  const departureCity = thread.threadable.departureAddress?.city;
  const deliveryCity = thread.threadable.deliveryAddress?.city;
  if (departureCity === undefined || deliveryCity === undefined) return null;

  return (
    <div>
      {departureCity} -{">"} {deliveryCity}
    </div>
  );
}
